// Event Dashboard tab wrapper. Sets the per-tenant context (slug from the URL)
// and loads the tenant's ticket-model config BEFORE the ported dashboard app
// renders/classifies data, then mounts it. The dashboard app itself is the
// faithful port of the .136 event-dashboard2 under ./eventDashboard/.

import { useParams, useLocation, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { useAuth } from '@/contexts/AuthContext.jsx';
import { setDashboardTenant, getApiBase, setVenueTz } from './eventDashboard/utils/dashboardContext';
import { setTicketModel, DEFAULT_TICKET_MODEL } from './eventDashboard/utils/ticketModel';
import EventDashboardApp from './eventDashboard/components/EventDashboard.jsx';

export default function EventDashboard() {
  const { tenantSlug } = useParams();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  // Set synchronously during render so child data calls (which fire in mount
  // effects, after render) always see the current tenant.
  setDashboardTenant(tenantSlug);

  // Load the per-tenant ticket model before mounting the app, so rate
  // classification uses the right vocabulary from the first detail load.
  // null config → built-in LIV default. Gate on the slug the config was loaded
  // for (readySlug) so a tenant SWITCH doesn't mount the app with the previous
  // tenant's model: on switch, readySlug (still the old slug) !== tenantSlug, so
  // we render null until the new config resolves — no transient, no double-mount.
  // Standalone PWA: advertise this tenant's manifest so the app is installable
  // and launches into the dashboard (display:standalone). Scoped per tenant.
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = `/api/event-dashboard/${tenantSlug}/manifest.webmanifest`;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, [tenantSlug]);

  const [readySlug, setReadySlug] = useState(null);
  useEffect(() => {
    if (!isAuthenticated) return; // unauthenticated → we redirect below; don't fire a 401
    let cancelled = false;
    setTicketModel(DEFAULT_TICKET_MODEL);
    apiFetch(`${getApiBase()}/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        if (d.ticketModel) setTicketModel(d.ticketModel);
        if (d.venueTz) setVenueTz(d.venueTz);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setReadySlug(tenantSlug); });
    return () => { cancelled = true; };
  }, [tenantSlug, isAuthenticated]);

  // Standalone route (outside Layout) → guard here so a logged-out deep link
  // returns to THIS page after SSO (router state.from), instead of bouncing to /.
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  if (readySlug !== tenantSlug) return null;
  return <EventDashboardApp key={tenantSlug} />;
}
